import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { groupedSubClassMap, SnakeMeta } from '@api/api/timeseries-schema';
import { RangeHorizon, ResampleIntervals } from '@chart';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { isEconomicSeries } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { config, webOnlyFeat } from '@terminal/config/app.config';
import { About } from '@terminal/modules/about/About';
import { LegacyEntityHeader } from '@terminal/modules/entity-header/LegacyEntityHeader';
import { OverviewEventFeed } from '@terminal/modules/overview-event-feed/OverviewEventFeed';
import { OverviewFinancials } from '@terminal/modules/overview-financials/OverviewFinancials';
import { ThemeKnowledgeGraph } from '@terminal/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { OverviewValuation } from '@terminal/modules/overview-valuation-metrics/OverviewValuationMetrics';
import { ChartLoading } from '@terminal/modules/simple-chart/components/chart-loading/ChartLoading';
import { ChartPrice } from '@terminal/modules/simple-chart/components/chart-price/ChartPrice';
import { SimpleChart } from '@terminal/modules/simple-chart/SimpleChart';
import {
  isCountryOrArea,
  useCountryGdp,
} from '@terminal/react-query-hooks/country-gdp/useCountryGdp';
import { useEarningsInsightsByEntities } from '@terminal/react-query-hooks/earnings/useEarningsInsightsByEntities/useEarningsInsightsByEntities';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import {
  isLongTail,
  useLongTailSeries,
} from '@terminal/react-query-hooks/long-tail-series/useLongTailSeries';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { useAssetOverviewNavigation } from '@terminal/widgets/asset-overview-widget/hooks/use-asset-overview-navigation/useAssetOverviewNavigation';
import { BrandsAndProductsSection } from '@terminal/widgets/knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { CompetitorsSection } from '@terminal/widgets/knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/competitors-section/CompetitorsSection';
import { EarningsInsightSection } from '@terminal/widgets/knowledge-graph-widget/components/earnings-insight-section/EarningsInsightSection';
import { GeographicExposureSection } from '@terminal/widgets/knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/geographic-exposure-section/GeographicExposureSection';
import { UpgradeToUnlock } from '@terminal/widgets/overview/components/upgrade-to-unlock/UpgradeToUnlock';
import styles from './LegacyAssetOverviewWidget.module.scss';

export const LEGACY_AO_DRAWER_SUB_CLASSES = new Set([
  ...groupedSubClassMap.fx,
  ...groupedSubClassMap.etfs,
  ...groupedSubClassMap.indexes,
  ...groupedSubClassMap.govBonds,
  ...groupedSubClassMap.commodities,
  AssetSubClassCode.EcoCentralBank,
]);

export interface LegacyAssetOverviewWidgetProps {
  entity: Entity;
  isSeriesLoading?: boolean;
  snakeMeta?: SnakeMeta;
  isDrawer?: boolean;
  source: PosthogTrackingSource;
  replaceHistoryOnNavigate?: boolean;
}

/**
 * previous version before AO pulse was implemented, still used for basic users
 */

export const AssetOverviewWidgetContent = ({
  entity,
  isDrawer = false,
  isSeriesLoading = false,
  snakeMeta: resolvedSnakeMeta,
  source,
  replaceHistoryOnNavigate = false,
}: LegacyAssetOverviewWidgetProps) => {
  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;

  const isBasicUser = useSubscription(state => state.isBasicUser());
  const { data: fetchedSnakeMeta } = useSnakeMeta(
    resolvedSnakeMeta ? '' : entity.default_snake
  );
  const snakeMeta = resolvedSnakeMeta ?? fetchedSnakeMeta;
  const isAoDrawerOverhaulEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5354-ao-drawer-overhaul')
  );

  const { byEntityTag, isLoading: isEarningsLoading } =
    useEarningsInsightsByEntities(isStock ? [entity.tag] : []);
  const {
    latestEarningsData,
    earningsInsight,
    isPreview = false,
  } = byEntityTag.get(entity.tag) ?? {};

  const { error: companyThemeError } = useCompanyTheme({
    entityTag: entity.tag,
    enabled: isStock,
  });

  const { navigateToChart } = useAssetOverviewNavigation(
    entity,
    source,
    replaceHistoryOnNavigate
  );

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneDay,
    horizon: isEconomicSeries(entity)
      ? RangeHorizon.OneYear
      : RangeHorizon.ThreeMonths,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const showDrawerFinancials = isStock && isDrawer;
  const headerClassName = isDrawer
    ? styles.styledEntityHeaderDrawer
    : styles.styledEntityHeader;

  const displayConnections = companyThemeError?.status !== 404 && isStock;
  const hasChart = !isLongTail(entity) || !!entity.default_snake.trim();
  const hideUnsupportedSections =
    isAoDrawerOverhaulEnabled &&
    LEGACY_AO_DRAWER_SUB_CLASSES.has(entity.sub_class);

  return (
    <div className={styles.container} data-testid="asset-overview-widget">
      <LegacyEntityHeader
        entity={entity}
        truncateLongTitle={isDrawer}
        truncateAtLength={40}
        isDrawer={isDrawer}
        headerButton={<></>}
        hideButtonNode={config.app.isTeamsApp}
        className={headerClassName}
      />
      <div className={styles.contentContainer}>
        {isSeriesLoading ? (
          <ChartLoading isLegacyVariant={false} isChatMessage={false} />
        ) : (
          hasChart && (
            <div className={styles.chartChipSelectorWrapper}>
              <ChartPrice entity={entity} snakeMeta={snakeMeta} />
              <SimpleChart
                entity={entity}
                initialChartSettings={chartSettings}
                isLegacyVariant={false}
                hasLineColourBasedOnPrice={!isEconomicSeries(entity)}
                onClickFullscreen={webOnlyFeat(navigateToChart)}
                maxHorizon={
                  isDrawer ? RangeHorizon.ThreeYears : RangeHorizon.TenYears
                }
                eventsConfig={{
                  TMI: true,
                }}
                source={source}
              />
            </div>
          )
        )}
        {isStock && (
          <EarningsInsightSection
            entity={entity}
            earningsData={latestEarningsData}
            earningsInsight={earningsInsight}
            isLoading={isEarningsLoading}
            isPreview={isPreview}
          />
        )}
        {isBasicUser ? (
          <UpgradeToUnlock />
        ) : (
          !hideUnsupportedSections && (
            <ErrorBoundaryWrapper>
              <OverviewEventFeed
                entity={entity}
                isDrawer={isDrawer}
                source={source}
              />
            </ErrorBoundaryWrapper>
          )
        )}

        {isDrawer && (
          <About
            entity={entity}
            variant="secondary"
            showMoreDescription
            limit={300}
          />
        )}

        {displayConnections && (
          <>
            <ThemeKnowledgeGraph
              entityTag={entity.tag}
              source={source}
              isInDrawer={isDrawer}
            />
            <CompetitorsSection
              entityTag={entity.tag}
              source={source}
              isInDrawer={isDrawer}
            />
            <GeographicExposureSection
              entityTag={entity.tag}
              source={source}
              isInDrawer={isDrawer}
            />
            <BrandsAndProductsSection
              entityTag={entity.tag}
              source={source}
              isInDrawer={isDrawer}
            />
          </>
        )}
        {showDrawerFinancials && (
          <>
            <OverviewFinancials entity={entity} isDrawer={isDrawer} />
            <OverviewValuation entity={entity} />
          </>
        )}
      </div>
    </div>
  );
};

const CountryAssetOverviewWidget = (props: LegacyAssetOverviewWidgetProps) => {
  const resolved = useCountryGdp(props.entity);
  return <AssetOverviewWidgetContent {...props} {...resolved} />;
};

const LongTailAssetOverviewWidget = (props: LegacyAssetOverviewWidgetProps) => {
  const resolved = useLongTailSeries(props.entity);
  return <AssetOverviewWidgetContent {...props} {...resolved} />;
};

export const LegacyAssetOverviewWidget = (
  props: LegacyAssetOverviewWidgetProps
) =>
  isCountryOrArea(props.entity) ? (
    <CountryAssetOverviewWidget {...props} />
  ) : isLongTail(props.entity) ? (
    <LongTailAssetOverviewWidget {...props} />
  ) : (
    <AssetOverviewWidgetContent {...props} />
  );
